import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LuArrowLeft,
  LuMic,
  LuSend,
  LuPlus,
  LuTrash2,
  LuHistory,
  LuHouse,
  LuLayers,
  LuSettings,
  LuMessageCircle,
  LuPlay,
  LuX,
} from 'react-icons/lu';
import XiTopNav from '../components/xi/XiTopNav';
import XiBackdrop from '../components/xi/XiBackdrop';
import XiChatMascot from '../components/xi/XiChatMascot';
import XiBotAvatar from '../components/xi/XiBotAvatar';
import useSessionUser from '../hooks/useSessionUser';
import { sendVoiceToSpeechBot } from '../components/api';

const STORAGE_KEY = 'xi_chat_session_v1';

const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function makeConversation() {
  return {
    id: uid(),
    title: 'New conversation',
    messages: [],
    context: '',
    createdAt: Date.now(),
  };
}

function loadConversations() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.map((c) => ({
          ...c,
          messages: (c.messages || []).map((m) => ({ ...m, audioUrl: null })),
        }));
      }
    }
  } catch {
    /* ignore malformed session data */
  }
  return [makeConversation()];
}

function clockOf(ts) {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function audioUrlFromBase64(b64) {
  if (!b64 || typeof b64 !== 'string') return null;
  if (b64.startsWith('data:')) return b64;
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
  } catch {
    return null;
  }
}

export default function Chatbot() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useSessionUser();

  const [initialConversations] = useState(loadConversations);
  const [conversations, setConversations] = useState(initialConversations);
  const [activeId, setActiveId] = useState(() => initialConversations[0].id);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notice, setNotice] = useState('');
  const [draft, setDraft] = useState('');
  const [playingId, setPlayingId] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const autoStopRef = useRef(null);
  const audioRef = useRef(null);
  const threadEndRef = useRef(null);
  const textareaRef = useRef(null);

  const active = useMemo(
    () => conversations.find((c) => c.id === activeId) || conversations[0],
    [conversations, activeId],
  );

  // Session-only persistence (no backend history contract exists).
  useEffect(() => {
    try {
      const safe = conversations.map((c) => ({
        ...c,
        messages: c.messages.map((m) => ({ ...m, audioUrl: null })),
      }));
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
    } catch {
      /* ignore quota / private mode */
    }
  }, [conversations]);

  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [active?.messages?.length, isProcessing]);

  useEffect(() => {
    return () => {
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const patchActive = useCallback(
    (updater) => {
      setConversations((prev) =>
        prev.map((c) => (c.id === activeId ? updater(c) : c)),
      );
    },
    [activeId],
  );

  const addMessage = useCallback(
    (message) => {
      patchActive((c) => {
        const messages = [...c.messages, { id: uid(), at: Date.now(), ...message }];
        let title = c.title;
        if (title === 'New conversation' && message.role === 'assistant') {
          title = message.text.slice(0, 48) + (message.text.length > 48 ? '…' : '');
        }
        return { ...c, messages, title };
      });
    },
    [patchActive],
  );

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'recording') mr.stop();
    setIsRecording(false);
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    setNotice('');
    if (!isAuthenticated) {
      setNotice('Sign in to talk to Xi.');
      return;
    }
    if (isProcessing) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mr = new window.MediaRecorder(stream);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, { type: 'audio/ogg' });
        if (!blob.size) {
          setNotice('That recording was empty. Try again.');
          return;
        }

        addMessage({
          role: 'user',
          text: 'Voice message',
          audioUrl: URL.createObjectURL(blob),
          kind: 'voice',
        });

        setIsProcessing(true);
        try {
          const data = await sendVoiceToSpeechBot(blob, { context: active?.context || '' });
          const reply =
            (typeof data?.response === 'string' && data.response.trim()) ||
            (typeof data?.message === 'string' && data.message.trim()) ||
            '';
          if (!reply) {
            setNotice('Xi returned an empty reply. Please try again.');
            return;
          }
          const audioUrl = audioUrlFromBase64(data?.audio_base64);
          addMessage({ role: 'assistant', text: reply, audioUrl });
          if (data?.context) {
            patchActive((c) => ({ ...c, context: data.context }));
          }
        } catch (err) {
          setNotice('Xi could not be reached. Check your connection and try again.');
        } finally {
          setIsProcessing(false);
        }
      };

      mr.start();
      setIsRecording(true);
      autoStopRef.current = setTimeout(() => stopRecording(), 30000);
    } catch (err) {
      setNotice('Microphone access was denied. Allow microphone access and try again.');
    }
  }, [active?.context, addMessage, isAuthenticated, isProcessing, patchActive, stopRecording]);

  function handleSendText(e) {
    e.preventDefault();
    if (!draft.trim()) return;
    setNotice('Text chat is not connected yet — Xi can hear you. Tap the microphone to talk.');
    if (textareaRef.current) textareaRef.current.focus();
  }

  function playAudio(message) {
    if (!message.audioUrl) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const audio = new Audio(message.audioUrl);
    audioRef.current = audio;
    setPlayingId(message.id);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
  }

  function newConversation() {
    const conv = makeConversation();
    setConversations((prev) => [conv, ...prev]);
    setActiveId(conv.id);
    setDrawerOpen(false);
    setNotice('');
  }

  function clearConversation() {
    patchActive((c) => ({ ...c, messages: [], title: 'New conversation' }));
    setNotice('');
  }

  function deleteConversation(id) {
    setConversations((prev) => {
      const next = prev.filter((c) => c.id !== id);
      if (!next.length) {
        const conv = makeConversation();
        setActiveId(conv.id);
        return [conv];
      }
      if (id === activeId) setActiveId(next[0].id);
      return next;
    });
  }

  const statusText = isRecording
    ? 'Listening…'
    : isProcessing
      ? 'Thinking…'
      : 'Online · voice';

  return (
    <div className="xi-page-shell">
      <div className="xi-app-frame xi-chat-frame">
        <XiBackdrop variant="chat" />

        <div className="xi-chat-chrome">
          <XiTopNav
            user={user}
            isAuthenticated={isAuthenticated}
            onRequireLogin={() => navigate('/login')}
            onLogout={logout}
            onDeactivate={() => navigate('/delete')}
            onOpenSubscription={() => navigate('/plans')}
          />
        </div>

        <div className="xi-chat-page">
          <div className="xi-chat">
            {drawerOpen && (
              <div
                className="xi-chat__drawer-scrim"
                role="presentation"
                onClick={() => setDrawerOpen(false)}
              />
            )}

            <aside className={`xi-chat__sidebar${drawerOpen ? ' is-open' : ''}`} aria-label="Conversations">
              <button type="button" className="xi-chat__new" onClick={newConversation}>
                <LuPlus aria-hidden="true" />
                New conversation
              </button>

              <p className="xi-chat__sidebar-label">History</p>

              <div className="xi-chat__history xi-scroll">
                {conversations.length === 0 ? (
                  <p className="xi-chat__history-empty">No conversations yet.</p>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`xi-chat__history-item${conv.id === activeId ? ' is-active' : ''}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setActiveId(conv.id);
                        setDrawerOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setActiveId(conv.id);
                          setDrawerOpen(false);
                        }
                      }}
                    >
                      <LuMessageCircle aria-hidden="true" />
                      <span>{conv.title}</span>
                      <button
                        type="button"
                        aria-label={`Delete ${conv.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.id);
                        }}
                        style={{
                          marginLeft: 'auto',
                          display: 'grid',
                          placeItems: 'center',
                          width: 22,
                          height: 22,
                          borderRadius: 6,
                          border: 0,
                          background: 'transparent',
                          color: 'inherit',
                          cursor: 'pointer',
                          opacity: 0.55,
                          flex: 'none',
                        }}
                      >
                        <LuX style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="xi-chat__sidebar-foot">
                <Link to="/" className="xi-chat__sidebar-link">
                  <LuHouse aria-hidden="true" /> Home
                </Link>
                <Link to="/plans" className="xi-chat__sidebar-link">
                  <LuLayers aria-hidden="true" /> Plans
                </Link>
                <Link to="/settings" className="xi-chat__sidebar-link">
                  <LuSettings aria-hidden="true" /> Settings
                </Link>
              </div>
            </aside>

            <div className="xi-chat__main">
              <div className="xi-chat__topbar">
                <button
                  type="button"
                  className="xi-icon-btn xi-chat__drawer-btn"
                  aria-label="Conversation history"
                  onClick={() => setDrawerOpen(true)}
                >
                  <LuHistory />
                </button>

                <Link to="/" className="xi-icon-btn xi-chat__drawer-btn" aria-label="Back to Home">
                  <LuArrowLeft />
                </Link>

                <div className="xi-chat__identity">
                  <span className="xi-chat__identity-avatar" aria-hidden="true">
                    <XiBotAvatar size="sm" />
                  </span>
                  <div className="xi-chat__identity-text">
                    <span className="xi-chat__identity-name">Xi</span>
                    <span className="xi-chat__identity-status">
                      <i aria-hidden="true" />
                      {statusText}
                    </span>
                  </div>
                </div>

                <div className="xi-chat__topbar-actions">
                  <button
                    type="button"
                    className="xi-icon-btn"
                    aria-label="Clear conversation"
                    title="Clear conversation"
                    onClick={clearConversation}
                  >
                    <LuTrash2 />
                  </button>
                  <button
                    type="button"
                    className="xi-icon-btn"
                    aria-label="New conversation"
                    title="New conversation"
                    onClick={newConversation}
                  >
                    <LuPlus />
                  </button>
                </div>
              </div>

              <div className="xi-chat__messages xi-scroll">
                {active?.messages?.length ? (
                  <div className="xi-chat__thread">
                    {active.messages.map((message) => (
                      <div
                        key={message.id}
                        className={`xi-msg xi-msg--${message.role}`}
                      >
                        <span
                          className={`xi-msg__avatar${message.role === 'user' ? ' xi-msg__avatar--user' : ''}`}
                          aria-hidden="true"
                        >
                          {message.role === 'user' ? (
                            (user?.displayName || 'You').slice(0, 2).toUpperCase()
                          ) : (
                            <XiBotAvatar size="sm" />
                          )}
                        </span>

                        <div className="xi-msg__body">
                          <div className="xi-msg__bubble">{message.text}</div>

                          {message.audioUrl && (
                            <div className="xi-msg__audio">
                              <button
                                type="button"
                                className="xi-msg__play"
                                aria-label={playingId === message.id ? 'Playing audio' : 'Play audio'}
                                onClick={() => playAudio(message)}
                              >
                                <LuPlay />
                              </button>
                              <span>
                                {message.kind === 'voice'
                                  ? 'Your recording'
                                  : playingId === message.id
                                    ? 'Playing reply…'
                                    : 'Play Xi\u2019s reply'}
                              </span>
                            </div>
                          )}

                          <div className="xi-msg__meta">{clockOf(message.at)}</div>
                        </div>
                      </div>
                    ))}

                    {isProcessing && (
                      <div className="xi-msg xi-msg--assistant">
                        <span className="xi-msg__avatar" aria-hidden="true">
                          <XiBotAvatar size="sm" />
                        </span>
                        <div className="xi-msg__body">
                          <div className="xi-msg__bubble">
                            <span className="xi-typing" aria-label="Xi is thinking">
                              <i /><i /><i />
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={threadEndRef} />
                  </div>
                ) : (
                  <div className="xi-chat-empty">
                    <XiChatMascot
                      className="xi-chat-empty__mascot"
                    />
                    <h2 className="xi-chat-empty__title">Hi, I&apos;m Xi.</h2>
                    <p className="xi-chat-empty__text">
                      Tap the microphone and tell me how you&apos;re feeling. I&apos;ll listen and
                      respond out loud.
                    </p>
                    <div className="xi-chat-empty__suggestions">
                      <span className="xi-chat-empty__chip">“I&apos;m feeling anxious”</span>
                      <span className="xi-chat-empty__chip">“Help me relax”</span>
                      <span className="xi-chat-empty__chip">“How am I doing this week?”</span>
                    </div>
                  </div>
                )}
              </div>

              {notice && (
                <div style={{ padding: '0 16px 8px' }}>
                  <div className="xi-error" role="status">
                    {notice}
                  </div>
                </div>
              )}

              <div className="xi-composer-wrap">
                <form className="xi-composer" onSubmit={handleSendText}>
                  <button
                    type="button"
                    className={`xi-composer__btn${isRecording ? ' xi-composer__btn--recording' : ''}`}
                    aria-label={isRecording ? 'Stop recording' : 'Start voice message'}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                  >
                    <LuMic />
                  </button>

                  <textarea
                    ref={textareaRef}
                    className="xi-composer__input"
                    rows={1}
                    placeholder={
                      isRecording
                        ? 'Listening…'
                        : 'Message Xi…'
                    }
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendText(e);
                      }
                    }}
                    aria-label="Message"
                  />

                  <div className="xi-composer__actions">
                    <button
                      type="submit"
                      className="xi-composer__btn xi-composer__btn--send"
                      aria-label="Send message"
                      disabled={!draft.trim()}
                      title="Text chat needs a backend endpoint that is not available yet"
                    >
                      <LuSend />
                    </button>
                  </div>
                </form>

                <p className="xi-composer__note">
                  Voice chat is live through the existing Xi speech service. Text chat is
                  disabled because no text-chat endpoint exists yet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
